import React, { createContext, useContext, useState, useEffect, useMemo, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

interface CompanyInfo {
  company_id: string;
  company_name: string;
  company_slug: string;
  association_role: string;
  is_active: boolean;
  logo_url?: string | null;
}

interface CompanyContextType {
  currentCompany: CompanyInfo | null;
  availableCompanies: CompanyInfo[];
  switchCompany: (companyId: string) => Promise<void>;
  refreshCompanies: () => Promise<void>;
  loading: boolean;
  error: string | null;
}

const CompanyContext = createContext<CompanyContextType | undefined>(undefined);

export const useCompany = () => {
  const context = useContext(CompanyContext);
  if (context === undefined) {
    throw new Error('useCompany must be used within a CompanyProvider');
  }
  return context;
};

interface CompanyProviderProps {
  children: ReactNode;
}

export const CompanyProvider: React.FC<CompanyProviderProps> = ({ children }) => {
  const { user, loading: authLoading } = useAuth();
  const [currentCompany, setCurrentCompany] = useState<CompanyInfo | null>(null);
  const [availableCompanies, setAvailableCompanies] = useState<CompanyInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchUserCompanies = async () => {
    // Don't fetch if auth is still loading
    if (authLoading) {
      console.log('CompanyContext: Auth still loading, waiting...');
      return;
    }

    if (!user) {
      console.log('CompanyContext: No user available for company fetching');
      setCurrentCompany(null);
      setAvailableCompanies([]);
      setLoading(false);
      return;
    }

    console.log('CompanyContext: Fetching companies for user:', user.id);
    
    // CRITICAL: Set loading to true at the START of the actual fetch
    // This prevents the race condition where loading is false during fetch
    setLoading(true);

    try {
      setError(null);
      const { data, error: fetchError } = await supabase.rpc('get_user_companies');
      
      console.log('CompanyContext: RPC call result:', { data, error: fetchError });
      
      if (fetchError) {
        console.error('CompanyContext: Error fetching user companies:', fetchError);
        setError('Failed to load companies');
        return;
      }

      console.log('CompanyContext: Available companies:', data);
      
      // RPC now returns logo_url directly, no need for secondary fetch
      if (data && data.length > 0) {
        setAvailableCompanies(data);
        // Find the currently active company
        const activeCompany = data.find(company => company.is_active);
        
        if (activeCompany) {
          setCurrentCompany(activeCompany);
          localStorage.setItem('selectedCompanyId', activeCompany.company_id);
        } else if (data.length > 0) {
          // If no active company but companies exist, set the first one as active
          await switchCompany(data[0].company_id);
        }
      } else {
        setAvailableCompanies(data || []);
        console.log('CompanyContext: No companies found for user');
      }
    } catch (err) {
      console.error('CompanyContext: Unexpected error fetching companies:', err);
      setError('Unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  const switchCompany = async (companyId: string) => {
    if (!user) return;

    console.log('CompanyContext: Switching to company:', companyId);

    try {
      setError(null);
      const { data, error: switchError } = await supabase.rpc('switch_active_company', {
        p_user_id: user.id,
        p_company_id: companyId
      });

      if (switchError) {
        console.error('CompanyContext: Error switching company:', switchError);
        setError('Failed to switch company');
        return;
      }

      // Update local state
      const selectedCompany = availableCompanies.find(company => company.company_id === companyId);
      if (selectedCompany) {
        // Fetch the latest logo_url for the selected company
        const { data: companyData } = await supabase
          .from('companies')
          .select('logo_url')
          .eq('id', companyId)
          .single();
        
        const updatedCompany = { 
          ...selectedCompany, 
          is_active: true,
          logo_url: companyData?.logo_url || null 
        };
        setCurrentCompany(updatedCompany);
        localStorage.setItem('selectedCompanyId', companyId);
        
        // Update available companies to reflect the change
        setAvailableCompanies(companies => 
          companies.map(company => ({
            ...company,
            is_active: company.company_id === companyId,
            logo_url: company.company_id === companyId ? (companyData?.logo_url || null) : company.logo_url
          }))
        );
      }

      // Dispatch event to notify other parts of the app
      window.dispatchEvent(new CustomEvent('company-switched', { 
        detail: { companyId } 
      }));
      
      console.log('CompanyContext: Successfully switched company');
    } catch (err) {
      console.error('CompanyContext: Unexpected error switching company:', err);
      setError('Unexpected error occurred');
    }
  };

  // Wait for auth to complete before fetching companies
  useEffect(() => {
    if (!authLoading) {
      console.log('CompanyContext: Auth completed, fetching companies for user:', user?.id);
      fetchUserCompanies();
    }
  }, [user, authLoading]);

  // Listen for company-created event to refresh companies list
  useEffect(() => {
    const handleCompanyCreated = () => {
      console.log('CompanyContext: Company created event received, refetching...');
      fetchUserCompanies();
    };

    window.addEventListener('company-created', handleCompanyCreated);
    return () => window.removeEventListener('company-created', handleCompanyCreated);
  }, [user]);

  // Removed redundant persistence effect - the effect at lines 163-168 already handles fetching when auth completes

  const contextValue = useMemo<CompanyContextType>(() => ({
    currentCompany,
    availableCompanies,
    switchCompany,
    refreshCompanies: fetchUserCompanies,
    loading,
    error
  }), [currentCompany, availableCompanies, loading, error]);

  return (
    <CompanyContext.Provider value={contextValue}>
      {children}
    </CompanyContext.Provider>
  );
};
